import { useRef, useCallback } from 'react';
import type { ProductCategory } from '@/mocks/products';

function CategoryRow({ category }: { category: ProductCategory }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div className="mb-12 md:mb-16">
      <div className="flex items-center justify-between mb-5 px-4 sm:px-6 lg:px-8 xl:px-12">
        <h2 className="text-xl md:text-2xl font-bold text-stone-900 tracking-tight">{category.name}</h2>
        <a
          href={category.url}
          target="_blank"
          rel="noopener nofollow"
          className="text-sm font-medium text-stone-500 hover:text-stone-900 transition-colors cursor-pointer flex items-center gap-1"
        >
          View All
          <i className="ri-arrow-right-s-line"></i>
        </a>
      </div>

      <div className="relative px-4 sm:px-6 lg:px-8 xl:px-12">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth hide-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {category.products.map((product) => (
            <a
              key={product.id}
              href={product.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex-shrink-0 w-[180px] md:w-[220px] snap-start cursor-pointer"
            >
              <div className="bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-stone-200 hover:shadow-lg transition-all duration-300">
                <div className="aspect-square overflow-hidden bg-stone-100 relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Minimalist%20gray%20product%20placeholder%20image%20with%20soft%20shadow%20on%20white%20background%2C%20clean%20modern%20design%2C%20neutral%20tones%2C%20simple%20geometric%20shapes%2C%20professional%20product%20photography%20style&width=300&height=300&seq=placeholder-01&orientation=squarish';
                    }}
                  />
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="text-xs md:text-sm font-medium text-stone-900 leading-snug line-clamp-2 mb-2 group-hover:text-stone-700 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm md:text-base font-bold text-stone-800">{product.price}</p>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-md border border-stone-100 flex items-center justify-center text-stone-700 hover:bg-white hover:shadow-lg transition-all cursor-pointer z-10 hidden md:flex"
          type="button"
          aria-label="Scroll left"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          onClick={() => scroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-md border border-stone-100 flex items-center justify-center text-stone-700 hover:bg-white hover:shadow-lg transition-all cursor-pointer z-10 hidden md:flex"
          type="button"
          aria-label="Scroll right"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>
    </div>
  );
}

export default function ProductShowcase({ categories }: { categories: ProductCategory[] }) {
  return (
    <section className="py-16 md:py-24 bg-stone-50">
      <div className="w-full">
        <div className="text-center mb-12 md:mb-16 px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">Shop the Best Finds</h2>
          <p className="text-stone-500 max-w-2xl mx-auto">
            Browse curated products from AllChinaBuy Spreadsheet. Click any item to view details and purchase.
          </p>
        </div>

        <div className="space-y-2 md:space-y-4">
          {categories.map((category) => (
            <CategoryRow key={category.key} category={category} />
          ))}
        </div>
      </div>
    </section>
  );
}